/**
 * Typed fetch client for the same-origin /api/v1/* proxy.
 *
 * Two surfaces:
 *   - `api` (browser): credentials-included fetch with automatic 401 token
 *     refresh and distinct 402 SUBSCRIPTION_ENDED detection.
 *   - `serverFetch` (RSC / route handlers): forwards the incoming request
 *     cookies to the backend through the proxy.
 *
 * Patterns reused from ai-dashboard/frontend/lib/api-client.ts (ApiError class,
 * single-flight refresh queue, session-expired event), adapted for the new
 * 3-tier role model and the subscription lockout.
 */

import type {
  AcceptInviteInput,
  AcceptInviteResponse,
  AccountSeriesResponse,
  ActivityResponse,
  AdAccountsResponse,
  AdInsightsResponse,
  AdsDatePreset,
  AgencyBrandingResponse,
  AgencyOverviewResponse,
  AnomaliesResponse,
  BrandingBySubdomainResponse,
  BrandingMeResponse,
  UpdateBrandingInput,
  AnalyticsSummaryResponse,
  AudienceResponse,
  CadenceResponse,
  FormatsResponse,
  GrowthResponse,
  HashtagsResponse,
  RecommendationsResponse,
  ReelsResponse,
  StoriesResponse,
  BrandedContentAdvertisableMediaResponse,
  BrandedContentPermissionsResponse,
  BrandedContentSearchResponse,
  CampaignsResponse,
  ClientAnalyticsResponse,
  ClientsResponse,
  ConnectionsResponse,
  ConnectionTestResult,
  ConnectorTypesResponse,
  ConnectorTypesAdminResponse,
  ConnectorTypeSetting,
  ConnectorTypeSettingsResponse,
  CreateConnectionInput,
  CreateInviteInput,
  CreateInviteResponse,
  CreateGoalRequest,
  CreateOrganizationInput,
  CreateScheduleInput,
  CreateShareInput,
  CreateShareResponse,
  CreateDigestInput,
  DigestsResponse,
  AlertSettingsResponse,
  SaveAlertSettingInput,
  EngagementCommentsResponse,
  EngagementFeedResponse,
  EngagementMediaResponse,
  SentimentCountsResponse,
  SLAResponse,
  CannedReply,
  CannedReplyInput,
  ModerationRule,
  ModerationRuleInput,
  Lead,
  CreateLeadInput,
  LeadStatus,
  AIReplyResponse,
  GoalsResponse,
  InboxConversationsResponse,
  InboxMessagesResponse,
  InsightsDays,
  InsightsResponse,
  InvitesResponse,
  Organization,
  OrganizationsResponse,
  Period,
  PlatformMetrics,
  PostsResponse,
  ProvisionUserInput,
  PublicInvite,
  PublicReportResponse,
  SchedulesResponse,
  SharesResponse,
  SocialGoal,
  UpdateConnectionInput,
  UpdateOrganizationInput,
  UpdateSubscriptionInput,
  User,
  UserConnection,
  OrgUsersResponse,
  OrgUserManagementResponse,
  OrgToolsResponse,
  CapabilityKind,
  OrgCapabilitiesResponse,
  AgencyCapabilitiesResponse,
  SetOrgToolResponse,
  OrgUsersListResponse,
  OrgManagedUser,
  CreateOrgUserInput,
  UpdateOrgUserInput,
  TenantHealthResponse,
  TenantHealthRow,
  ImpersonateResponse,
  AuditEventsResponse,
  AuditFilter,
  Board,
  BoardExecutionResult,
  BoardItem,
  BoardItemType,
  BoardItemUpdate,
  BoardLayoutUpdate,
  CreateSavedQueryInput,
  SavedQuery,
  SavedQueryExecuteResponse,
} from "./types";

const BASE_URL = "/api/v1";

// ─── Errors ───────────────────────────────────────────────────────

export class ApiError extends Error {
  status: number;
  code?: string;
  data: Record<string, unknown> | null;

  constructor(status: number, data: Record<string, unknown> | null) {
    const message = String(
      data?.error ?? data?.message ?? `Request failed with status ${status}`,
    );
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
    this.code = typeof data?.code === "string" ? data.code : undefined;
  }
}

/**
 * Thrown when the backend returns 402 with code SUBSCRIPTION_ENDED. The UI
 * catches this distinctly and routes to the lockout page; platform users never
 * trigger it because their requests have no org subscription to gate.
 */
export class SubscriptionEndedError extends ApiError {
  constructor(data: Record<string, unknown> | null) {
    super(402, data);
    this.name = "SubscriptionEndedError";
  }
}

export const SUBSCRIPTION_ENDED_CODE = "SUBSCRIPTION_ENDED";
/** Window event dispatched when a request returns 402 SUBSCRIPTION_ENDED. */
export const SUBSCRIPTION_ENDED_EVENT = "subscription:ended";
/** Window event dispatched when the session expires (refresh failed). */
export const SESSION_EXPIRED_EVENT = "auth:session-expired";

// ─── Browser client (single-flight refresh) ──────────────────────

let isRefreshing = false;
let waiters: Array<{ resolve: () => void; reject: (e: unknown) => void }> = [];

function drainWaiters(error: unknown | null) {
  waiters.forEach((w) => (error ? w.reject(error) : w.resolve()));
  waiters = [];
}

async function parseError(
  response: Response,
): Promise<Record<string, unknown> | null> {
  return (await response
    .json()
    .catch(() => null)) as Record<string, unknown> | null;
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  isRetry = false,
): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}`, {
    credentials: "include",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string> | undefined),
    },
  });

  if (response.ok) {
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  // 402 subscription lockout — surface distinctly so the UI can redirect.
  if (response.status === 402) {
    const data = await parseError(response);
    if (data?.code === SUBSCRIPTION_ENDED_CODE) {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(SUBSCRIPTION_ENDED_EVENT));
      }
      throw new SubscriptionEndedError(data);
    }
    throw new ApiError(402, data);
  }

  // 429 — rate limited by the backend. Throw ApiError(429) so per-call
  // handlers (and the generic error UI) can react.
  if (response.status === 429) {
    throw new ApiError(429, await parseError(response));
  }

  // 403 — permission denied (Casbin authz). Throw ApiError(403).
  if (response.status === 403) {
    throw new ApiError(403, await parseError(response));
  }

  // 401 — try a single token refresh, queueing concurrent calls behind it.
  if (response.status === 401 && !isRetry) {
    if (path === "/auth/refresh") {
      throw new ApiError(401, await parseError(response));
    }
    if (isRefreshing) {
      await new Promise<void>((resolve, reject) =>
        waiters.push({ resolve, reject }),
      );
      return request<T>(path, options, true);
    }

    isRefreshing = true;
    try {
      const refresh = await fetch(`${BASE_URL}/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!refresh.ok) throw new Error("refresh failed");
      drainWaiters(null);
      return request<T>(path, options, true);
    } catch (err) {
      drainWaiters(err);
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      }
      throw new ApiError(401, await parseError(response));
    } finally {
      isRefreshing = false;
    }
  }

  throw new ApiError(response.status, await parseError(response));
}

export function toQuery(
  params?: Record<string, string | number | boolean | undefined | null>,
): string {
  if (!params) return "";
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null) sp.append(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export const api = {
  get<T>(
    path: string,
    params?: Record<string, string | number | boolean | undefined | null>,
  ): Promise<T> {
    return request<T>(`${path}${toQuery(params)}`, { method: "GET" });
  },
  post<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(path, {
      method: "POST",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  },
  put<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(path, {
      method: "PUT",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  },
  patch<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(path, {
      method: "PATCH",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  },
  del<T>(path: string): Promise<T> {
    return request<T>(path, { method: "DELETE" });
  },
};

// ─── Typed endpoint helpers (browser) ─────────────────────────────

export const authApi = {
  login: (email: string, password: string) =>
    api.post<import("./types").LoginResponse>("/auth/login", {
      email,
      password,
    }),
  // Telegram Mini App sign-in: the raw initData string is validated + its HMAC
  // checked on the backend, which then issues the same session cookies as a
  // normal login. Only works for a Telegram account already linked + verified
  // to a user (see Settings → link Telegram).
  telegramLogin: (initData: string) =>
    api.post<import("./types").LoginResponse>("/auth/telegram-login", {
      initData,
    }),
  me: () => api.get<User>("/auth/me"),
  logout: () => api.post<{ message: string }>("/auth/logout"),
};

export const connectionsApi = {
  list: () => api.get<ConnectionsResponse>("/connections"),
  get: (id: number) => api.get<UserConnection>(`/connections/${id}`),
  create: (body: CreateConnectionInput) =>
    api.post<UserConnection>("/connections", body),
  patch: (id: number, body: UpdateConnectionInput) =>
    request<UserConnection>(`/connections/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  remove: (id: number) => api.del<{ ok: boolean }>(`/connections/${id}`),
  test: (id: number) =>
    api.post<ConnectionTestResult>(`/connections/${id}/test`),
};

export const connectorsApi = {
  types: () => api.get<ConnectorTypesResponse>("/connectors/types"),
  // Admin-only: every healthy connector type with its platform enabled flag and
  // live instance count. Backend gates on the admin role (403 otherwise).
  typesAll: () =>
    api.get<ConnectorTypesAdminResponse>("/connectors/types/all"),
};

/**
 * Admin-only platform switchboard for which connector TYPES tenants may use.
 * Connector types default to disabled; `update` upserts the enabled flag for a
 * given connector id. Backend gates on the admin role (403 otherwise).
 */
export const connectorTypeSettingsApi = {
  list: () =>
    api.get<ConnectorTypeSettingsResponse>("/settings/connector-types"),
  update: (connectorId: string, enabled: boolean) =>
    api.put<ConnectorTypeSetting>("/settings/connector-types", {
      connector_id: connectorId,
      enabled,
    }),
};

/**
 * "Connect with Instagram" (Instagram Business Login). This is a full-page
 * navigation, not a fetch: the browser hits the backend (via the /api/v1
 * proxy), the backend sets the CSRF state cookie and 302-redirects to
 * Instagram. instagramConnectStartUrl returns the URL to navigate to.
 * If the feature is not configured server-side the endpoint returns 503,
 * which surfaces as a non-redirect response the page can detect.
 */
export const instagramOAuth = {
  startUrl: () => `${BASE_URL}/connect/instagram/start`,
};

/**
 * "Connect Facebook (Ads)" (Facebook Login + Marketing API). Like the
 * Instagram flow this is a full-page navigation, not a fetch: the browser hits
 * the backend, which sets the CSRF state cookie and 302-redirects to Facebook.
 * startUrl returns the URL to navigate to; the feature returns 503 when the
 * MAIN Facebook app credentials are not configured server-side.
 * listAdAccounts / setAdAccount drive the ad-account picker once connected.
 */
export const metaOAuth = {
  startUrl: () => `${BASE_URL}/connect/meta/start`,
  listAdAccounts: (connectionId: number) =>
    api.get<AdAccountsResponse>("/connect/meta/adaccounts", {
      connection_id: connectionId,
    }),
  setAdAccount: (connectionId: number, adAccountId: string) =>
    api.post<{ connection_id: number; ad_account_id: string }>(
      "/connect/meta/adaccount",
      { connection_id: connectionId, ad_account_id: adAccountId },
    ),
  // Store the allow-list of ad-account ids to expose in the ads picker (empty = all).
  setVisibleAdAccounts: (connectionId: number, accountIds: string[]) =>
    api.put<{ connection_id: number; visible_account_ids: string[] }>(
      "/connect/meta/visible-adaccounts",
      { connection_id: connectionId, account_ids: accountIds },
    ),
};

/**
 * Facebook-Ads analytics (Marketing API, ads_read). accounts() lists the
 * connection token's ad accounts (live); insights() returns headline metrics
 * for the selected ad account; campaigns() lists campaigns with per-campaign
 * insights. All are tenant-scoped server-side (the connection is loaded under
 * the caller's tenant scope; a foreign connection_id yields 404; a connection
 * with no ad account selected yields 400).
 */
export const adsApi = {
  accounts: (connectionId: number) =>
    api.get<AdAccountsResponse>("/ads/accounts", {
      connection_id: connectionId,
    }),
  insights: (
    connectionId: number,
    datePreset: AdsDatePreset = "last_7d",
    level = "account",
  ) =>
    api.get<AdInsightsResponse>("/ads/insights", {
      connection_id: connectionId,
      date_preset: datePreset,
      level,
    }),
  campaigns: (connectionId: number, datePreset: AdsDatePreset = "last_7d") =>
    api.get<CampaignsResponse>("/ads/campaigns", {
      connection_id: connectionId,
      date_preset: datePreset,
    }),
  timeSeries: (connectionId: number, datePreset: AdsDatePreset = "last_7d") =>
    api.get<import("./types").AdTimeSeriesResponse>("/ads/insights/timeseries", {
      connection_id: connectionId,
      date_preset: datePreset,
    }),
  // Trigger a background snapshot (rolling window, or a full backfill the first
  // time) so persisted ad history is refreshed on demand.
  snapshot: (connectionId: number) =>
    api.post<{ status: string; connection_id: number }>(
      `/ads/snapshot?connection_id=${connectionId}`,
    ),
  // Convenience aliases mirroring the metaOAuth picker endpoints so callers can
  // discover the full ads surface from one object.
  listAdAccounts: (connectionId: number) => metaOAuth.listAdAccounts(connectionId),
  setAdAccount: (connectionId: number, adAccountId: string) =>
    metaOAuth.setAdAccount(connectionId, adAccountId),
};

export const analyticsApi = {
  account: (connectionId: number, period: Period, platform = "instagram") =>
    api.get<AccountSeriesResponse>("/analytics/account", {
      connection_id: connectionId,
      period,
      platform,
    }),
  posts: (connectionId: number, limit = 25, platform = "instagram") =>
    api.get<PostsResponse>("/analytics/posts", {
      connection_id: connectionId,
      limit,
      platform,
    }),
  audience: (connectionId: number, platform = "instagram") =>
    api.get<AudienceResponse>("/analytics/audience", {
      connection_id: connectionId,
      platform,
    }),
  activity: (connectionId: number, platform = "instagram") =>
    api.get<ActivityResponse>("/analytics/activity", {
      connection_id: connectionId,
      platform,
    }),
  // Audience growth composition over time (new follows vs unfollows per day +
  // net-growth + totals). Tenant-scoped: a foreign connection_id yields 404.
  growth: (connectionId: number, period: Period, platform = "instagram") =>
    api.get<GrowthResponse>("/analytics/audience/growth", {
      connection_id: connectionId,
      period,
      platform,
    }),

  // Content / post deep-analytics. Each is tenant-scoped server-side: a
  // connection_id outside the caller's scope yields 404.
  formats: (connectionId: number, period: Period, platform = "instagram") =>
    api.get<FormatsResponse>("/analytics/formats", {
      connection_id: connectionId,
      period,
      platform,
    }),
  hashtags: (
    connectionId: number,
    period: Period,
    top = 20,
    platform = "instagram",
  ) =>
    api.get<HashtagsResponse>("/analytics/hashtags", {
      connection_id: connectionId,
      period,
      top,
      platform,
    }),
  cadence: (connectionId: number, period: Period, platform = "instagram") =>
    api.get<CadenceResponse>("/analytics/cadence", {
      connection_id: connectionId,
      period,
      platform,
    }),
  reels: (connectionId: number, period: Period, platform = "instagram") =>
    api.get<ReelsResponse>("/analytics/reels", {
      connection_id: connectionId,
      period,
      platform,
    }),
  stories: (connectionId: number, period: Period, platform = "instagram") =>
    api.get<StoriesResponse>("/analytics/stories", {
      connection_id: connectionId,
      period,
      platform,
    }),

  // Current anomalies (metric swings vs the prior period) for a connection.
  // Tenant-scoped: a foreign connection_id yields 404.
  anomalies: (connectionId: number, period: Period, platform = "instagram") =>
    api.get<AnomaliesResponse>("/analytics/anomalies", {
      connection_id: connectionId,
      period,
      platform,
    }),

  // AI Insights inputs (rule-based, no LLM). recommendations returns the
  // structured suggestion list; summary returns the compact bundle the AI
  // narration/Q&A routes consume. Tenant-scoped: a foreign connection_id 404s.
  recommendations: (
    connectionId: number,
    period: Period,
    platform = "instagram",
  ) =>
    api.get<RecommendationsResponse>("/analytics/recommendations", {
      connection_id: connectionId,
      period,
      platform,
    }),
  summary: (connectionId: number, period: Period, platform = "instagram") =>
    api.get<AnalyticsSummaryResponse>("/analytics/summary", {
      connection_id: connectionId,
      period,
      platform,
    }),
};

/**
 * Public read-only reporting + share-link / schedule management.
 *
 * The CRUD helpers (createShare/listShares/revokeShare/listSchedules/
 * createSchedule/deleteSchedule) are authenticated + tenant-scoped server-side
 * (a foreign connection_id yields 404). publicReport(token) reads the
 * UNAUTHENTICATED public report through the same-origin proxy — it carries no
 * credentials requirement; an invalid / expired / revoked token yields 404.
 */
export const reportsApi = {
  createShare: (body: CreateShareInput) =>
    api.post<CreateShareResponse>("/reports/shares", body),
  listShares: (connectionId: number) =>
    api.get<SharesResponse>("/reports/shares", {
      connection_id: connectionId,
    }),
  revokeShare: (id: number) =>
    api.del<{ revoked: number }>(`/reports/shares/${id}`),

  listSchedules: (connectionId: number) =>
    api.get<SchedulesResponse>("/reports/schedules", {
      connection_id: connectionId,
    }),
  createSchedule: (body: CreateScheduleInput) =>
    api.post<{ schedule: import("./types").ReportSchedule }>(
      "/reports/schedules",
      body,
    ),
  deleteSchedule: (id: number) =>
    api.del<{ deleted: number }>(`/reports/schedules/${id}`),
};

/**
 * Scheduled AI digests: a periodic AI-written analytics briefing emailed on a
 * cadence. CRUD is authenticated + tenant-scoped server-side (a foreign
 * connection_id / digest id yields 404). The narrative is generated by the
 * backend digest job using the tenant's own AI provider + brand voice.
 */
export const digestsApi = {
  list: (connectionId: number) =>
    api.get<DigestsResponse>("/digests", { connection_id: connectionId }),
  create: (body: CreateDigestInput) =>
    api.post<{ digest: import("./types").AIDigestSchedule }>("/digests", body),
  remove: (id: number) => api.del<{ deleted: number }>(`/digests/${id}`),
};

/**
 * Per-user anomaly-alert subscriptions — the opt-in rows that turn anomaly
 * alerts ON and choose who they email.
 *
 * These are PERSONAL: the server scopes every call to the calling user, so
 * `list` returns only your own subscriptions and you cannot read or edit a
 * colleague's, even inside the same organization. `save` is an upsert keyed by
 * (connection, platform, period), so toggling alerts on and off reuses one row.
 */
export const alertSettingsApi = {
  list: (connectionId?: number) =>
    api.get<AlertSettingsResponse>(
      "/alert-settings",
      connectionId ? { connection_id: connectionId } : undefined,
    ),
  save: (body: SaveAlertSettingInput) =>
    api.put<{ alert_setting: import("./types").AlertSetting }>(
      "/alert-settings",
      body,
    ),
  remove: (id: number) => api.del<{ deleted: number }>(`/alert-settings/${id}`),
};

/**
 * Fetch the public read-only report for a share token through the same-origin
 * /api/v1 proxy. No auth is required (the token is the credential). Returns the
 * report payload; an invalid / expired / revoked token throws ApiError(404).
 */
export function publicReport(token: string): Promise<PublicReportResponse> {
  return api.get<PublicReportResponse>(
    `/reports/public/${encodeURIComponent(token)}`,
  );
}

/**
 * Instagram comment management (Meta instagram_business_manage_comments).
 * media() lists posts to pick from; comments() reads live comments on a post;
 * reply/hide/remove mutate them; feed() reads webhook-captured comments. All
 * are tenant-scoped server-side (the connection is loaded under the caller's
 * tenant scope; a foreign connection_id yields 404).
 */
export const engagementApi = {
  media: (connectionId: number, limit = 25) =>
    api.get<EngagementMediaResponse>("/engagement/media", {
      connection_id: connectionId,
      limit,
    }),
  comments: (connectionId: number, mediaId: string) =>
    api.get<EngagementCommentsResponse>("/engagement/comments", {
      connection_id: connectionId,
      media_id: mediaId,
    }),
  reply: (connectionId: number, commentId: string, message: string) =>
    api.post<{ id: string }>("/engagement/comments/reply", {
      connection_id: connectionId,
      comment_id: commentId,
      message,
    }),
  hide: (connectionId: number, commentId: string, hide: boolean) =>
    api.post<{ comment_id: string; hidden: boolean }>(
      "/engagement/comments/hide",
      { connection_id: connectionId, comment_id: commentId, hide },
    ),
  remove: (connectionId: number, commentId: string) =>
    api.del<{ deleted: string }>(
      `/engagement/comments${toQuery({
        connection_id: connectionId,
        comment_id: commentId,
      })}`,
    ),
  feed: (connectionId: number, limit = 50) =>
    api.get<EngagementFeedResponse>("/engagement/comments/feed", {
      connection_id: connectionId,
      limit,
    }),
  sentiment: (connectionId: number, period = "all") =>
    api.get<SentimentCountsResponse>("/engagement/sentiment", {
      connection_id: connectionId,
      period,
    }),
  sla: (connectionId: number, period = "all") =>
    api.get<SLAResponse>("/engagement/sla", {
      connection_id: connectionId,
      period,
    }),
};

/**
 * Saved reply templates (canned replies). Tenant-scoped server-side; the
 * engagement + inbox reply UIs insert these with one click.
 */
export const cannedRepliesApi = {
  list: (category = "") =>
    api.get<{ replies: CannedReply[] }>("/engagement/canned", {
      category: category || undefined,
    }),
  create: (body: CannedReplyInput) =>
    api.post<{ reply: CannedReply }>("/engagement/canned", body),
  update: (id: number, body: CannedReplyInput) =>
    api.put<{ reply: CannedReply }>(`/engagement/canned/${id}`, body),
  remove: (id: number) =>
    api.del<{ deleted: number }>(`/engagement/canned/${id}`),
};

/**
 * Keyword moderation rules. Tenant-scoped server-side; the webhook evaluates
 * active rules on comment ingest (hide → hides on Instagram + flags; flag →
 * flags only; lead → DM lead detection).
 */
export const moderationApi = {
  list: () =>
    api.get<{ rules: ModerationRule[] }>("/engagement/moderation-rules"),
  create: (body: ModerationRuleInput) =>
    api.post<{ rule: ModerationRule }>("/engagement/moderation-rules", body),
  update: (id: number, body: ModerationRuleInput) =>
    api.put<{ rule: ModerationRule }>(
      `/engagement/moderation-rules/${id}`,
      body,
    ),
  remove: (id: number) =>
    api.del<{ deleted: number }>(`/engagement/moderation-rules/${id}`),
};

/**
 * Lead capture. Tenant-scoped server-side; inbound DMs auto-capture leads on
 * keyword match (deduped per conversation). list/create/status/delete here.
 */
export const leadsApi = {
  list: (connectionId?: number, status = "") =>
    api.get<{ leads: Lead[] }>("/leads", {
      connection_id: connectionId || undefined,
      status: status || undefined,
    }),
  create: (body: CreateLeadInput) => api.post<{ lead: Lead }>("/leads", body),
  setStatus: (id: number, status: LeadStatus) =>
    api.patch<{ lead: Lead }>(`/leads/${id}`, { status }),
  remove: (id: number) => api.del<{ deleted: number }>(`/leads/${id}`),
  // Pull Meta/Facebook Lead-Ad form submissions into leads (source=meta_ads).
  syncMeta: (connectionId: number) =>
    api.post<{ connection_id: number; created: number }>(
      `/leads/sync-meta?connection_id=${connectionId}`,
    ),
};

/**
 * AI reply suggestions (Next.js route, NOT the /api/v1 proxy). Posts the
 * incoming comment/DM text and returns 2-3 on-brand reply suggestions resolved
 * from the tenant's configured LLM. A no-provider tenant gets a 400 with a
 * helpful message (surfaced as the thrown Error.message).
 */
export async function suggestReply(
  text: string,
  context: "comment" | "dm",
  connectionId?: number,
): Promise<AIReplyResponse> {
  const res = await fetch("/api/ai/reply", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ text, context, connection_id: connectionId }),
  });
  const data = (await res.json().catch(() => ({}))) as
    | AIReplyResponse
    | { error?: string };
  if (!res.ok) {
    throw new Error(
      ("error" in data && data.error) || "Failed to get reply suggestions",
    );
  }
  return data as AIReplyResponse;
}

/**
 * Instagram DM inbox (Meta instagram_business_manage_messages).
 * conversations() lists DM threads; messages() reads a thread; send() posts a
 * reply. All are tenant-scoped server-side (the connection is loaded under the
 * caller's tenant scope; a foreign connection_id yields 404).
 */
export const inboxApi = {
  conversations: (connectionId: number) =>
    api.get<InboxConversationsResponse>("/inbox/conversations", {
      connection_id: connectionId,
    }),
  messages: (connectionId: number, conversationId: string, limit = 50) =>
    api.get<InboxMessagesResponse>("/inbox/messages", {
      connection_id: connectionId,
      conversation_id: conversationId,
      limit,
    }),
  send: (connectionId: number, recipientId: string, text: string) =>
    api.post<{ id: string }>("/inbox/send", {
      connection_id: connectionId,
      recipient_id: recipientId,
      text,
    }),
};

/**
 * Instagram Branded Content / Partnership Ads (Meta
 * instagram_branded_content_brand / _creator / _ads_brand). Read-only on the
 * "Instagram API with Facebook Login" surface, so connectionId is a Facebook
 * (META_ADS_CONNECTOR_ID) connection — NOT an Instagram one. permissions() lists
 * partnership-ad permission grants involving the linked Instagram business
 * account; advertisableMedia() lists media eligible to run as partnership ads;
 * search() discovers IG accounts that posted branded content in a date window.
 * All are tenant-scoped server-side (a foreign connection_id yields 404; a
 * connection with no linked Instagram Business account yields 400). Granting
 * permissions and generating partnership "ad codes" are UI-only flows (no Graph
 * POST endpoint), so this surface is read-only.
 */
export const brandedContentApi = {
  permissions: (connectionId: number, creatorUsername = "") =>
    api.get<BrandedContentPermissionsResponse>("/branded-content/permissions", {
      connection_id: connectionId,
      creator_username: creatorUsername || undefined,
    }),
  advertisableMedia: (
    connectionId: number,
    opts: {
      adCode?: string;
      creatorUsername?: string;
      onlyAllowlisted?: boolean;
      onlyRecommended?: boolean;
    } = {},
  ) =>
    api.get<BrandedContentAdvertisableMediaResponse>(
      "/branded-content/advertisable-media",
      {
        connection_id: connectionId,
        ad_code: opts.adCode || undefined,
        creator_username: opts.creatorUsername || undefined,
        only_allowlisted: opts.onlyAllowlisted ? "true" : undefined,
        only_recommended: opts.onlyRecommended ? "true" : undefined,
      },
    ),
  search: (
    connectionId: number,
    igUsername: string,
    dateMin = "",
    dateMax = "",
  ) =>
    api.get<BrandedContentSearchResponse>("/branded-content/search", {
      connection_id: connectionId,
      ig_username: igUsername,
      date_min: dateMin || undefined,
      date_max: dateMax || undefined,
    }),
};

/**
 * Dual-timezone Instagram insights for a date window. `pacific` matches the
 * numbers shown inside the Instagram app exactly; `local` is computed in the
 * account's own timezone. Call with a `days` preset (7 | 14 | 30 | 90) OR an
 * explicit `since`/`until` (YYYY-MM-DD). Tenant-scoped server-side: a foreign
 * connection_id yields 404.
 */
export const socialApi = {
  insights: (
    connectionId: number,
    range: { days: InsightsDays } | { since: string; until: string },
  ) =>
    api.get<InsightsResponse>("/social/insights", {
      connection_id: connectionId,
      ...("days" in range
        ? { days: range.days }
        : { since: range.since, until: range.until }),
    }),
};

export const goalsApi = {
  list: (connectionId: number, platform = "instagram") =>
    api.get<GoalsResponse>("/analytics/goals", {
      connection_id: connectionId,
      platform,
    }),
  create: (body: CreateGoalRequest) =>
    api.post<{ goal: SocialGoal }>("/analytics/goals", body),
  remove: (id: number) =>
    api.del<{ deleted: number }>(`/analytics/goals/${id}`),
};

export const platformApi = {
  metrics: () => api.get<PlatformMetrics>("/platform/metrics"),
  organizations: () => api.get<OrganizationsResponse>("/platform/organizations"),
  createOrganization: (body: CreateOrganizationInput) =>
    api.post<Organization>("/platform/organizations", body),
  updateOrganization: (id: number, body: UpdateOrganizationInput) =>
    api.put<Organization>(`/platform/organizations/${id}`, body),
  deleteOrganization: (id: number) =>
    api.del<void>(`/platform/organizations/${id}`),
  updateSubscription: (id: number, body: UpdateSubscriptionInput) =>
    api.put<Organization>(`/platform/organizations/${id}/subscription`, body),
  provisionUser: (id: number, body: ProvisionUserInput) =>
    api.post<User>(`/platform/organizations/${id}/users`, body),
  // Per-org white-label branding (superadmin brands a direct client).
  getOrgBranding: (id: number) =>
    api.get<AgencyBrandingResponse>(`/platform/organizations/${id}/branding`),
  setOrgBranding: (id: number, body: UpdateBrandingInput) =>
    api.put<AgencyBrandingResponse>(
      `/platform/organizations/${id}/branding`,
      body,
    ),

  // ── User management ──
  // Per-org user console + per-user account actions (password reset,
  // block/unblock, role change, delete). All backend-enforced (you cannot
  // block, re-role, or delete your own account — the backend returns 400).
  // For an agency org the response includes the agency's users plus its client
  // orgs' users; for a client org, just that client's users.
  orgUserManagement: (id: number) =>
    api.get<OrgUserManagementResponse>(
      `/platform/organizations/${id}/user-management`,
    ),
  deleteUser: (id: number) =>
    api.del<{ status: string }>(`/platform/users/${id}`),
  setUserPassword: (id: number, password: string) =>
    api.post<{ status: string }>(`/platform/users/${id}/password`, { password }),
  setUserBlocked: (id: number, blocked: boolean) =>
    api.post<{ status: string; blocked: boolean }>(
      `/platform/users/${id}/blocked`,
      { blocked },
    ),
  setUserRole: (id: number, role: string) =>
    api.put<{ status: string; role: string }>(`/platform/users/${id}/role`, {
      role,
    }),

  // ── Support console ──
  // Tenant-health dashboard: cross-tenant rows + single-org detail.
  health: () => api.get<TenantHealthResponse>("/platform/health"),
  orgHealth: (id: number) =>
    api.get<{ tenant: TenantHealthRow }>(
      `/platform/organizations/${id}/health`,
    ),
  // Org users (for the per-user Impersonate action).
  orgUsers: (id: number) =>
    api.get<OrgUsersResponse>(`/platform/organizations/${id}/users`),
  // Audited impersonation: start (operator-only) + stop (callable while
  // impersonating — the backend identifies the operator from the stashed
  // impersonator_token cookie).
  impersonate: (userId: number) =>
    api.post<ImpersonateResponse>("/platform/impersonate", { user_id: userId }),
  stopImpersonation: () =>
    api.post<{ impersonating: boolean }>("/platform/impersonate/stop"),
  // Platform-scoped audit-log viewer (filterable + paginated). Surfaces the
  // cross-tenant audit trail (including impersonation events) for operators.
  audit: (filter?: AuditFilter) =>
    api.get<AuditEventsResponse>("/platform/audit/events", {
      page: filter?.page,
      limit: filter?.limit,
      event_type: filter?.event_type,
      actor_id: filter?.actor_id,
      from: filter?.from,
      to: filter?.to,
    }),
};

/**
 * Per-org AI tool gating (platform console → org detail → Tools). Platform-only,
 * Casbin-gated to platform_owner/support. `list` returns every AI tool from every
 * connector with its enabled state for the org (opt-in: enabled:false means the
 * org's AI cannot use it); `set` upserts a single org/tool toggle.
 */
/**
 * Per-organization capability toggles — product areas AND connector types,
 * selected by `kind`.
 *
 * Two consoles over one model. The platform endpoints set the CEILING for any
 * organization; the agency endpoints set an agency's choice for its OWN
 * organizations within that ceiling. Effective = the AND of the two, and an
 * organization with no stored row has everything enabled.
 *
 * Disabling a connector hides it from the picker and refuses NEW connections of
 * that type. It does NOT stop collection on connections that already exist —
 * withdrawing one of those is an explicit delete.
 */
export const platformCapabilitiesApi = {
  list: (orgId: number, kind: CapabilityKind) =>
    api.get<OrgCapabilitiesResponse>(
      `/platform/organizations/${orgId}/capabilities`,
      { kind },
    ),
  set: (orgId: number, kind: CapabilityKind, key: string, enabled: boolean) =>
    api.put<OrgCapabilitiesResponse>(
      `/platform/organizations/${orgId}/capabilities`,
      { kind, key, enabled },
    ),
};

/** Agency-side of the same model, scoped to the caller's own organizations. */
export const agencyCapabilitiesApi = {
  list: (kind: CapabilityKind) =>
    api.get<AgencyCapabilitiesResponse>("/agency/capabilities", { kind }),
  set: (
    orgId: number,
    kind: CapabilityKind,
    key: string,
    enabled: boolean,
  ) =>
    api.put<AgencyCapabilitiesResponse>("/agency/capabilities", {
      org_id: orgId,
      kind,
      key,
      enabled,
    }),
};

export const platformOrgToolsApi = {
  list: (orgId: number) =>
    api.get<OrgToolsResponse>(`/platform/organizations/${orgId}/tools`),
  set: (orgId: number, toolName: string, enabled: boolean) =>
    api.put<SetOrgToolResponse>(`/platform/organizations/${orgId}/tools`, {
      tool_name: toolName,
      enabled,
    }),
};

/**
 * Org-admin user management (Settings → Users). Gated backend-side to
 * agency_admin + client_admin, and scoped to the caller's manageable org(s):
 * list() returns every user across those orgs plus the orgs themselves; the
 * mutations create/edit/reset-password/delete a single user. The backend
 * enforces scope + tier (400/403/404 with `{error}`) — e.g. you cannot assign a
 * role above the target org's tier, touch a user outside your org, or delete /
 * re-role your own account.
 */
export const orgUsersApi = {
  list: () => api.get<OrgUsersListResponse>("/org/users"),
  create: (body: CreateOrgUserInput) =>
    api.post<OrgManagedUser>("/org/users", body),
  update: (id: number, body: UpdateOrgUserInput) =>
    api.put<OrgManagedUser>(`/org/users/${id}`, body),
  setPassword: (id: number, password: string) =>
    api.post<{ status: string }>(`/org/users/${id}/password`, { password }),
  remove: (id: number) => api.del<{ status: string }>(`/org/users/${id}`),
};

export const agencyApi = {
  clients: () => api.get<ClientsResponse>("/agency/clients"),
  createClient: (name: string) =>
    api.post<Organization>("/agency/clients", { name }),
  provisionClientUser: (id: number, body: ProvisionUserInput) =>
    api.post<User>(`/agency/clients/${id}/users`, body),
  clientAnalytics: (id: number) =>
    api.get<ClientAnalyticsResponse>(`/agency/clients/${id}/analytics`),
  overview: (period: Period = "week", platform = "instagram") =>
    api.get<AgencyOverviewResponse>("/agency/analytics/overview", {
      period,
      platform,
    }),
  invites: () => api.get<InvitesResponse>("/agency/invites"),
  createInvite: (body: CreateInviteInput) =>
    api.post<CreateInviteResponse>("/agency/invites", body),
  revokeInvite: (id: number) =>
    api.del<{ message: string }>(`/agency/invites/${id}`),
};

// White-label agency branding. The agency editor reads/writes its own row; the
// `me` resolution returns the branding that applies to the signed-in caller
// (own or parent agency). `bySubdomain` is the PUBLIC login-page lookup — it is
// unauthenticated, but the same-origin proxy forwards it fine; a 404 means no
// agency claims that subdomain (the caller falls back to the default brand).
export const brandingApi = {
  get: () => api.get<AgencyBrandingResponse>("/agency/branding"),
  update: (body: UpdateBrandingInput) =>
    api.put<AgencyBrandingResponse>("/agency/branding", body),
  me: () => api.get<BrandingMeResponse>("/branding/me"),
  bySubdomain: (subdomain: string) =>
    api.get<BrandingBySubdomainResponse>("/branding/by-subdomain", {
      subdomain,
    }),
  // Upload a small logo/favicon image; returns a (relative, domain-independent)
  // serve URL to save as logo_url/favicon_url.
  uploadAsset: async (
    file: File,
  ): Promise<{ id: number; url: string; content_type: string }> => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`${BASE_URL}/branding/assets`, {
      method: "POST",
      body: fd,
      credentials: "include",
    });
    if (!res.ok) {
      throw new ApiError(res.status, await parseError(res));
    }
    return res.json();
  },
};

// Public client self-onboarding via an agency-issued invite link. These calls
// are unauthenticated (the token is the credential); accept sets the auth
// cookies on the response so the new client user is logged straight in.
export const inviteApi = {
  get: (token: string) =>
    api.get<PublicInvite>(`/onboarding/invite/${encodeURIComponent(token)}`),
  accept: (token: string, body: AcceptInviteInput) =>
    api.post<AcceptInviteResponse>(
      `/onboarding/invite/${encodeURIComponent(token)}/accept`,
      body,
    ),
};

// Boards = the backend's custom `Dashboard` resource, surfaced in the UI at
// /boards so it does not collide with /dashboard (Instagram Analytics).
//
// Widget data binding: a widget carries a `saved_query_id`, and executing it
// re-runs that saved query through its connector and returns FRESH rows. The
// `cache_id` (a frozen DuckDB snapshot) and legacy file `query_hash` branches
// remain for widgets created by chat; the direct-DB `connection_id` branch
// errors out by design. A binding that no longer resolves — the saved query was
// deleted, or is no longer visible to the board's owner — answers 422 on
// execute, which the UI renders inside the one affected widget.
export const boardsApi = {
  // The backend returns a nil slice as JSON `null` for an empty list, so coalesce
  // to [] here — the Board[] type is otherwise a lie and every caller would have
  // to guard `.length`/`.map` against null.
  list: () => api.get<Board[] | null>("/dashboards").then((r) => r ?? []),
  get: (id: number) => api.get<Board>(`/dashboards/${id}`),
  create: (title: string) => api.post<Board>("/dashboards", { title }),
  update: (id: number, title: string) =>
    api.put<{ message: string }>(`/dashboards/${id}`, { title }),
  remove: (id: number) => api.del<{ message: string }>(`/dashboards/${id}`),

  // `saved_query_id` is the live binding this UI always sends; the other three
  // binding fields exist for widgets created elsewhere (chat) and are not set
  // from here. The backend rejects a create with none of the four.
  createItem: (body: {
    dashboard_id: number;
    type: BoardItemType;
    title?: string;
    sql_query?: string;
    saved_query_id?: number;
    cache_id?: number;
    query_hash?: string;
    connection_id?: number;
    chart_config_json?: string;
    refresh_frequency?: string;
  }) => api.post<BoardItem>("/dashboards/items", body),
  // WHOLE-OBJECT REPLACE — see `BoardItemUpdate`. The body type is deliberately
  // NOT `Partial<…>`: a partial body silently clears every field it omits,
  // including the widget's saved-query binding. Build it with
  // `buildBoardItemUpdate(item, changes)`.
  updateItem: (id: number, body: BoardItemUpdate) =>
    api.put<{ message: string }>(`/dashboards/items/${id}`, body),
  removeItem: (id: number) =>
    api.del<{ message: string }>(`/dashboards/items/${id}`),
  // A saved-query-bound widget re-runs its query on every execute, so `force`
  // only matters to legacy hash-resolved widgets (cache_id widgets always
  // replay their bound snapshot).
  executeItem: (id: number, force = false) =>
    api.post<BoardExecutionResult>(
      `/dashboards/items/${id}/execute?force=${force ? "true" : "false"}`,
    ),
  // PUBLIC: token-scoped widget execution for the anonymous shared view. The
  // token is the credential and the backend resolves the item strictly inside
  // the dashboard that token names. There is deliberately NO `force` on this
  // path, and it is rate limited to 30/min per IP.
  executeSharedItem: (token: string, itemId: number) =>
    api.post<BoardExecutionResult>(
      `/shared-dashboards/${encodeURIComponent(token)}/items/${itemId}/execute`,
    ),
  // Batch layout write. UNLIKE `updateItem` this is NOT a whole-object replace:
  // the backend issues a column-scoped `UPDATE … SET position_x, position_y,
  // width, height`, so it cannot touch a widget's saved-query binding. Drag and
  // resize therefore go through here and are safe by construction.
  saveLayout: (dashboardId: number, updates: BoardLayoutUpdate[]) =>
    api.put<{ message: string }>(`/dashboards/${dashboardId}/layout`, {
      dashboard_id: dashboardId,
      updates,
    }),

  // Sharing. `share` is tenant-restricted server-side: a target outside the
  // sharer's org (or an agency's child client orgs) comes back as a 400 whose
  // ApiError message is surfaced to the user verbatim.
  share: (id: number, userIds: number[]) =>
    api.post<{ message: string }>(`/dashboards/${id}/share`, {
      user_ids: userIds,
    }),
  sharedWithMe: () =>
    api.get<Board[] | null>("/dashboards/shared-with-me").then((r) => r ?? []),
  // PUBLIC: the share token is the credential, no session required.
  getShared: (token: string) =>
    api.get<Board>(`/shared-dashboards/${encodeURIComponent(token)}`),
  cloneShared: (token: string) =>
    api.post<Board>(`/dashboards/shared/${encodeURIComponent(token)}/clone`),
};

// Reusable SQL queries that board widgets bind to. Mounted under /settings on
// the backend, but consumed by the boards UI rather than the settings pages.
export const savedQueriesApi = {
  // Nil slice -> JSON null when empty; coalesce so the SavedQuery[] type holds.
  list: () =>
    api.get<SavedQuery[] | null>("/settings/saved-queries").then((r) => r ?? []),
  create: (body: CreateSavedQueryInput) =>
    api.post<SavedQuery>("/settings/saved-queries", body),
  update: (id: number, body: Partial<CreateSavedQueryInput>) =>
    api.put<SavedQuery>(`/settings/saved-queries/${id}`, body),
  remove: (id: number) =>
    api.del<{ message: string }>(`/settings/saved-queries/${id}`),
  // Runs the query through its connector and caches the rows in DuckDB; the
  // returned cache_id is what a new widget is bound to.
  execute: (id: number) =>
    api.post<SavedQueryExecuteResponse>(
      `/settings/saved-queries/${id}/execute`,
    ),
};

export const onboardingApi = {
  status: () => api.get<{ is_onboarded: boolean }>("/onboarding/status"),
  register: (body: {
    name: string;
    email: string;
    phone: string;
    license_key?: string;
  }) =>
    api.post<{ message: string; registration_token: string }>(
      "/onboarding/register",
      body,
    ),
  verify: (body: {
    registration_token: string;
    email_code: string;
    phone_code: string;
  }) => api.post<{ message: string }>("/onboarding/verify", body),
  createAdmin: (body: {
    first_name: string;
    last_name: string;
    email: string;
    password: string;
  }) => api.post<{ message: string }>("/onboarding/admin", body),
};
